import React, { useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useDispatch } from 'react-redux';
import { useAllBook, useBookOrder } from 'store/books/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import { handleCloneLoteria } from 'store/bingo/cloneLoteriaThunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import Loading from 'components/Loading';
import { dropdownForResourceType } from 'utils/config/reference';
import { resource_types } from 'store/extraResource/constants';
import { RESOURCE_BOOK_ID_MAP } from 'modules/assignments/helper';
import { map } from 'lodash';
import ResourceTypeSelector from 'modules/extraResource/admin/resourceTypeSelector';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentOrgPermission } from 'store/organizations/selectors';
import get from 'lodash/get';
import find from 'lodash/find';
import { useUserRole } from 'store/users/selectors';

const CloneLoteriaModal = ({ open, onClose, cloneGame, bookId, pageId, onSuccess }) => {
    const showAlert = useAlert();
    const dispatch = useDispatch();
    const booksAllList = useAllBook();
    
    const [cloneResourceType, setCloneResourceType] = useState('ebook');
    const [cloneBook, setCloneBook] = useState(bookId || '');
    const [clonePage, setClonePage] = useState('');
    const [cloneLoader, setCloneLoader] = useState(false);
    
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState({});
    const [selectedMaskId, setSelectedMaskId] = useState('');
    const [warningData, setWarningData] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const bookUnitList = useBookUnits();
    const currentClass = useCurrentClass();
    const archived_group = useMemo(() => currentClass.archived_group || [], [currentClass])
    const orgPermissions = useCurrentOrgPermission();
    const bookOrder = useBookOrder()
    const { is_super_admin, currentUser } = useUserRole();
    const orderedBook = useMemo(() => {
        let order_group = [];
        const books_group_permission = get(orgPermissions, "books_group_permission", []);
        (bookOrder || []).forEach((order) => {
            const class_books_group = currentClass.books_group || []
            if (archived_group.includes(order.key)) return null;
            if (books_group_permission.length == 0 || books_group_permission.includes(order.key)) {
                const has_book = find(class_books_group, k => k == order.key);
                if (has_book) {
                    const result = [...(order && order.order && order.order.map((chapter) => {
                            const book = find(booksAllList, ["id", chapter]);
                            if (book) {
                                return book;
                            }
                            return null;
                        }).filter(Boolean))]
                    order_group.push({
                        key: order.key,
                        title: order.title,
                        color: order.color,
                        books: result
                    })
                }
            }
        });
        return order_group;
    }, [currentClass, orgPermissions, booksAllList]);
    const allowedBooks = useMemo(() => {
        return Array.from(new Set(orderedBook.map(order => order.books).flat()));
    }, [orderedBook]);


    const safeCloneGame = useMemo(() => {
        if (!cloneGame) return null;
        return {
            id: cloneGame?.id || null,
            criteria: cloneGame?.criteria || 'Untitled'
        };
    }, [cloneGame?.id, cloneGame?.criteria]);

    const availableBooksForClone = useMemo(() => {
        const bks = Array.isArray(booksAllList) ? booksAllList : [];
        if(is_super_admin) {
            return bks.filter(bk =>  bk.isNotAssignable === false && bk.active === true);
        }
        return bks.filter(bk => allowedBooks.find(alb => alb.id == bk.id));
    }, [booksAllList, allowedBooks,is_super_admin]);

    const pagesForSelectedCloneBook = useMemo(() => {
        if (!cloneBook || !availableBooksForClone.length) return [];
        const selectedBook = availableBooksForClone.find(b => b.id === cloneBook);
        const pages = selectedBook?.pages || [];
        if (!Array.isArray(pages)) return [];
        return pages
            .map((p) => {
                if (typeof p === 'number') {
                    return { value: p, label: `Pg. ${p}` };
                }
                const id = p.pageId || p.id;
                if (!id && id !== 0) return null;
                const label = p.NumberMasked || p.displayName || `Pg. ${id}`;
                return { value: id, label };
            })
            .filter((p) => p && !(cloneBook === bookId && p.value === pageId));
    }, [availableBooksForClone, cloneBook, bookId, pageId]);

    const isBookUnitType = () => {
        const { balcon, conectándome, comunidad, informate } = resource_types;
        return cloneResourceType === balcon || 
               cloneResourceType === conectándome || 
               cloneResourceType === comunidad || 
               cloneResourceType === informate;
    };

    const needsMaskIdDropdown = () => {
        return isBookUnitType() || cloneResourceType === resource_types.AP || cloneResourceType === resource_types.story;
    };

    const handleResourceTypeChange = (value) => {
        setCloneResourceType(value);
        setSelectedItems([]);
        setSelectedConfig({});
        setSelectedMaskId('');
        setClonePage('');
        if (value === 'ebook') {
            setCloneBook(bookId || '');
        } else {
            setCloneBook('');
        }
    };

    const getBookIdForResourceType = () => {
        if (cloneResourceType === 'ebook') {
            return cloneBook;
        }
        if (isBookUnitType()) {
            return RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
        }
        if (cloneResourceType === resource_types.AP && selectedConfig?.selectedCategoryAP?.chapter_ids) {
            return selectedConfig.selectedCategoryAP.chapter_ids;
        }
        if (cloneResourceType === resource_types.story && selectedItems && selectedItems.length > 0) {
            const storyItem = selectedItems[0];
            if (storyItem.bookId) {
                return storyItem.bookId;
            }
        }
        if (cloneResourceType === resource_types.resource && selectedItems && selectedItems.length > 0) {
            return selectedItems[0].id || null;
        }
        if (selectedItems && selectedItems.length > 0) {
            const firstItem = selectedItems[0];
            if (firstItem.bookId) {
                return firstItem.bookId;
            }
        }
        return RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
    };

    const getMaskIdFromSelection = () => {
        if (cloneResourceType === 'ebook') {
            return clonePage;
        }
        if (needsMaskIdDropdown() && selectedMaskId) {
            return selectedMaskId;
        }
        if (cloneResourceType === resource_types.resource && selectedItems && selectedItems.length > 0) {
            return selectedItems[0].id || null;
        }
        if (selectedItems && selectedItems.length > 0) {
            const firstItem = selectedItems[0];
            return firstItem.id || firstItem.pageId || null;
        }
        return null;
    };

    const getMaskIdOptionsForSelectedCover = useMemo(() => {
        if (!needsMaskIdDropdown() || !selectedItems || selectedItems.length === 0) {
            return [];
        }
        
        let startPage = null;
        let finishPage = null;
        let bookId = null;

        if (cloneResourceType === resource_types.story) {
            const storyItem = selectedItems[0];
            const configLevel = selectedConfig?.level;
            const levels = storyItem?.levels;
            
            if (!configLevel || !levels || !Array.isArray(levels)) {
                return [];
            }
            
            const matchedLevel = levels.find((l) => {
                const levelName = l?.level?.toLowerCase().trim();
                const configLevelName = configLevel?.toLowerCase().trim();
                return levelName === configLevelName;
            });
            
            if (!matchedLevel || !matchedLevel.start || !matchedLevel.finish) {
                return [];
            }
            
            startPage = parseInt(matchedLevel.start, 10);
            finishPage = parseInt(matchedLevel.finish, 10);
            bookId = storyItem.bookId;
        } else {
            const selectedCover = selectedItems[0];
            startPage = parseInt(selectedCover.start, 10);
            finishPage = parseInt(selectedCover.finish, 10);
            
            if (cloneResourceType === resource_types.AP) {
                bookId = selectedConfig?.selectedCategoryAP?.chapter_ids;
            } else {
                bookId = RESOURCE_BOOK_ID_MAP[cloneResourceType];
            }
        }
        if (!startPage || !finishPage || isNaN(startPage) || isNaN(finishPage) || !bookId) {
            return [];
        }

        const selectedBook = booksAllList.find(b => {
            const bookIdNum = Number(bookId);
            const bIdNum = Number(b.id);
            return bIdNum === bookIdNum || String(b.id) === String(bookId) || b.id === bookId;
        });

        if (!selectedBook || !selectedBook.pages) return [];

        const pagesInRange = selectedBook.pages.filter((p) => {
            const pageId = typeof p === 'number' ? p : (p.pageId || p.id);
            const pageNum = parseInt(pageId, 10);
            return pageNum >= startPage && pageNum <= finishPage;
        });

        return pagesInRange.map((p, index) => {
            const pageId = typeof p === 'number' ? p : (p.pageId || p.id);
            const pageNum = parseInt(pageId, 10);
            const maskId = pageNum - startPage + 1;
            return { 
                value: pageId.toString(), 
                label: maskId.toString(),
                maskId: maskId.toString()
            };
        });
    }, [selectedItems, cloneResourceType, availableBooksForClone, selectedConfig]);

    const handleCloneSubmit = async (confirm = false) => {
        if (!safeCloneGame || !safeCloneGame.id) {
            showAlert("Please select a game to clone", "error");
            return;
        }

        if (cloneResourceType === 'ebook') {
            if (!cloneBook || !clonePage) {
                showAlert("Please select book and page", "error");
                return;
            }
        } else {
            if (!selectedItems || selectedItems.length === 0) {
                showAlert("Please select a resource", "error");
                return;
            }
            if (needsMaskIdDropdown() && !selectedMaskId) {
                showAlert("Please select a mask ID", "error");
                return;
            }
        }

        try {
            setCloneLoader(true);
            const bookIdValue = getBookIdForResourceType();
            const pageIdValue = getMaskIdFromSelection();
            
            if (!bookIdValue || !pageIdValue) {
                showAlert("Missing book_id or page_id", "error");
                setCloneLoader(false);
                return;
            }
            
            const payload = {
                loteria_id: safeCloneGame.id,
                book_id: bookIdValue,
                page_id: pageIdValue,
                confirm,
                role: currentUser?.role || '',
                user_id: currentUser?.id || ''
            };
            const result = await dispatch(handleCloneLoteria(payload));
            setCloneLoader(false);

            if (result?.warning === true) {
                setWarningData({
                    message: result.message || "If you want to clone again same loteria you already clone it previously",
                    existing_clones: result.existing_clones || 0
                });
                setShowWarning(true);
                return;
            }

            if (result?.success === true) {
                showAlert("Lotería cloned successfully!", "success");
                handleClose();
                if (onSuccess) {
                    await onSuccess(result.data);
                }
            } else {
                showAlert(result?.error || "Failed to clone lotería", "error");
            }
        } catch (error) {
            setCloneLoader(false);
            const errorMessage = error?.message || "An error occurred while cloning lotería";
            showAlert(errorMessage, "error");
        }
    };

    const handleConfirmClone = async () => {
        setShowWarning(false);
        await handleCloneSubmit(true);
    };

    const handleClose = () => {
        setCloneResourceType('ebook');
        setCloneBook(bookId || '');
        setClonePage('');
        setSelectedItems([]);
        setSelectedConfig({});
        setSelectedMaskId('');
        setWarningData(null);
        setShowWarning(false);
        onClose();
    };

    const isFormValid = () => {
        if (cloneResourceType === 'ebook') {
            return cloneBook && clonePage;
        }
        if (needsMaskIdDropdown()) {
            return selectedItems && selectedItems.length > 0 && selectedMaskId;
        }
        return selectedItems && selectedItems.length > 0;
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Clone Lotería</DialogTitle>
                <DialogContent>
                    <div style={{ marginTop: '16px' }}>
                        <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                            {`Select resource type to clone "${safeCloneGame?.criteria || 'Untitled'}"`}
                        </p>
                        <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
                            <InputLabel>Resource Type</InputLabel>
                            <Select
                                value={cloneResourceType}
                                label="Resource Type"
                                onChange={(e) => handleResourceTypeChange(e.target.value)}
                            >
                                {map(dropdownForResourceType, (element, i) => (
                                    <MenuItem key={i} value={element?.value}>
                                        {element?.label}
                                    </MenuItem>
                                ))}
                                <MenuItem value="ebook">Ebook</MenuItem>
                            </Select>
                        </FormControl>

                        {cloneResourceType === 'ebook' ? (
                            <>
                                <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
                                    <InputLabel>Book</InputLabel>
                                    <Select
                                        value={cloneBook}
                                        label="Book"
                                        onChange={(e) => {
                                            setCloneBook(e.target.value);
                                            setClonePage('');
                                        }}
                                    >
                                        {availableBooksForClone.map((bk) => (
                                            <MenuItem key={bk.id} value={bk.id}>
                                                {bk.description || bk.title || bk.name || `Book ${bk.id}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Page</InputLabel>
                                    <Select
                                        value={clonePage}
                                        label="Page"
                                        onChange={(e) => setClonePage(e.target.value)}
                                    >
                                        {pagesForSelectedCloneBook.map((p) => (
                                            <MenuItem key={p.value} value={p.value}>
                                                {p.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        ) : (
                            <>
                                <ResourceTypeSelector
                                    data={selectedItems}
                                    type={cloneResourceType}
                                    config={selectedConfig}
                                    handleSubmit={(item, additionalResource) => {
                                        setSelectedItems(item);
                                        setSelectedConfig({
                                            ...selectedConfig,
                                            ...(additionalResource && cloneResourceType === resource_types.AP && {
                                                selectedCategoryAP: additionalResource,
                                            }),
                                            ...(additionalResource && cloneResourceType === resource_types.resource && {
                                                linkResource: additionalResource,
                                            }),
                                            ...(additionalResource && cloneResourceType === resource_types.story && {
                                                level: additionalResource,
                                            }),
                                        });
                                    }}
                                    pageId={pageId}
                                />
                                {selectedItems && selectedItems.length > 0 && (
                                    <>
                                        {needsMaskIdDropdown() && (
                                            <FormControl variant="outlined" fullWidth style={{ marginTop: 16 }}>
                                                <InputLabel>Page Id</InputLabel>
                                                <Select
                                                    value={selectedMaskId}
                                                    label="Page Id"
                                                    onChange={(e) => setSelectedMaskId(e.target.value)}
                                                >
                                                    {getMaskIdOptionsForSelectedCover.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        <div style={{ marginTop: 16, padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                            <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>Selected Resource:</p>
                                            {isBookUnitType() || cloneResourceType === resource_types.AP ? (
                                                <p style={{ fontSize: '14px' }}>{selectedItems[0]?.shortName || selectedItems[0]?.displayName}</p>
                                            ) : cloneResourceType === resource_types.resource ? (
                                                <p style={{ fontSize: '14px' }}>{selectedItems[0]?.displayName}</p>
                                            ) : cloneResourceType === resource_types.story ? (
                                                <p style={{ fontSize: '14px' }}>{selectedItems[0]?.title}</p>
                                            ) : (
                                                <p style={{ fontSize: '14px' }}>{selectedItems[0]?.shortName || selectedItems[0]?.displayName}</p>
                                            )}
                                            {!needsMaskIdDropdown() && (
                                                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                    Mask ID: {getMaskIdFromSelection()}
                                                </p>
                                            )}
                                            {needsMaskIdDropdown() && selectedMaskId && (
                                                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                    Selected Page ID: {selectedMaskId}
                                                </p>
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={(e) => {
                            try {
                                e?.preventDefault?.();
                                handleCloneSubmit();
                            } catch (err) {
                                console.error('Error in clone submit:', err?.message);
                                showAlert("An error occurred while cloning", "error");
                            }
                        }}
                        variant="contained"
                        disabled={cloneLoader || !isFormValid()}
                    >
                        {cloneLoader ? <Loading size={15} white /> : 'Clone'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showWarning}
                onClose={() => setShowWarning(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon sx={{ color: '#ff9800' }} />
                        <Typography variant="h6">Duplicate Clone Detected</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {warningData?.message || "If you want to clone again same loteria you already clone it previously"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            You have already cloned this Lotería {warningData?.existing_clones || 0} time(s) to this location.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowWarning(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmClone}
                        variant="contained"
                        color="warning"
                        disabled={cloneLoader}
                    >
                        {cloneLoader ? <Loading size={15} white /> : 'Yes, Clone Again'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CloneLoteriaModal;

